import React from 'react'
import { FaUsers, FaBullseye, FaHeart, FaAward, FaHandshake, FaLeaf } from 'react-icons/fa'

const About = () => {
  const values = [
    {
      icon: FaHandshake,
      title: 'Solidarité',
      description: 'Nous croyons en la force de l\'union et de l\'entraide entre les membres de notre coopérative.'
    },
    {
      icon: FaLeaf,
      title: 'Durabilité',
      description: 'Nous nous engageons pour une agriculture respectueuse de l\'environnement et des générations futures.'
    },
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'Nous visons la qualité dans tous nos produits et services pour satisfaire nos clients.'
    },
    {
      icon: FaHeart,
      title: 'Authenticité',
      description: 'Nous valorisons nos traditions agricoles tout en embrassant l\'innovation responsable.'
    }
  ]

  const timeline = [
    {
      year: '2009',
      title: 'Création de la coopérative',
      description: 'Fondation de la SCOOP - CG 1 GEA R.SL par un groupe de 25 agriculteurs visionnaires de Saint-Louis.'
    },
    {
      year: '2012',
      title: 'Premier centre de transformation',
      description: 'Ouverture de notre première unité de transformation pour valoriser les productions locales.'
    },
    {
      year: '2015',
      title: 'Diversification vers l\'élevage',
      description: 'Lancement des activités d\'élevage bovin et ovin pour diversifier nos sources de revenus.'
    },
    {
      year: '2018',
      title: 'Développement de l\'aquaculture',
      description: 'Création de nos premiers bassins d\'aquaculture pour la production de poissons d\'eau douce.'
    },
    {
      year: '2021',
      title: 'Certification biologique',
      description: 'Obtention de la certification agriculture biologique pour une partie de nos productions.'
    },
    {
      year: '2024',
      title: 'Expansion régionale',
      description: 'Extension de nos activités vers 12 régions avec plus de 350 membres actifs.'
    }
  ]

  const leadership = [
    {
      name: 'Mamadou Diallo',
      position: 'Président',
      description: 'Agriculteur expérimenté, il dirige la coopérative depuis sa création.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Aïssatou Sow',
      position: 'Vice-Présidente',
      description: 'Spécialiste en transformation agroalimentaire et développement rural.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b5d47db3?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Ousmane Kane',
      position: 'Secrétaire Général',
      description: 'Expert en gestion coopérative et formation des producteurs.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Fatou Diop',
      position: 'Trésorière',
      description: 'Gestionnaire financière avec 20 ans d\'expérience dans le secteur agricole.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    }
  ]

  return (
    <div className="About">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Qui sommes-nous ?
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
              Découvrez l'histoire, la mission et les valeurs qui guident notre coopérative 
              agricole depuis plus de 15 ans
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <div className="mb-12">
                <div className="flex items-center mb-4">
                  <FaBullseye className="text-3xl text-primary-500 mr-4" />
                  <h2 className="text-3xl font-bold text-gray-900">Notre Mission</h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Promouvoir une agriculture durable et prospère dans la région de Saint-Louis 
                  en rassemblant les producteurs locaux autour de valeurs communes de solidarité, 
                  d'innovation et de respect de l'environnement.
                </p>
              </div>
              <div>
                <div className="flex items-center mb-4">
                  <FaUsers className="text-3xl text-primary-500 mr-4" />
                  <h2 className="text-3xl font-bold text-gray-900">Notre Vision</h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Devenir la coopérative agricole de référence au Sénégal, reconnue pour 
                  la qualité de ses produits, l'innovation de ses pratiques et son impact 
                  positif sur le développement rural et la sécurité alimentaire.
                </p>
              </div>
            </div>
            <div data-aos="fade-left">
              <img
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Vision de la coopérative"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ces valeurs fondamentales guident nos actions quotidiennes et orientent 
              nos décisions stratégiques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <div
                  key={index}
                  className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-2xl text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Histoire
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Retracez les étapes clés de notre développement et nos principales réalisations
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-primary-200"></div>

            {timeline.map((event, index) => (
              <div
                key={index}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'justify-start' : 'justify-end'
                } mb-12`}
                data-aos={index % 2 === 0 ? 'fade-right' : 'fade-left'}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                  <div className="bg-white p-6 rounded-2xl shadow-lg">
                    <div className="text-2xl font-bold text-primary-600 mb-2">
                      {event.year}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {event.title}
                    </h3>
                    <p className="text-gray-600">
                      {event.description}
                    </p>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Équipe Dirigeante
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rencontrez les leaders qui guident notre coopérative vers l'excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 font-medium mb-3">
                    {member.position}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section-padding bg-primary-500">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div data-aos="fade-up" data-aos-delay="100">
              <div className="text-4xl font-bold mb-2">350+</div>
              <p className="text-lg opacity-90">Membres actifs</p>
            </div>
            <div data-aos="fade-up" data-aos-delay="200">
              <div className="text-4xl font-bold mb-2">15</div>
              <p className="text-lg opacity-90">Années d'expérience</p>
            </div>
            <div data-aos="fade-up" data-aos-delay="300">
              <div className="text-4xl font-bold mb-2">2500</div>
              <p className="text-lg opacity-90">Tonnes produites/an</p>
            </div>
            <div data-aos="fade-up" data-aos-delay="400">
              <div className="text-4xl font-bold mb-2">12</div>
              <p className="text-lg opacity-90">Régions desservies</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About 